import * as React from "react";
import CardExpandable from "./CardExpandable";
import styles = require("./style.less");
import cn from "classnames";
import { FormSectionBase, FormSectionBaseProps } from "components/form/Sections/FormSectionBase";
import Expandable, { ExpandableProps, ExpandableContainerProps } from "components/Expandable/Expandable";
import { OverflowMenu } from "components/Menu";
import { MenuItem } from "components/Menu/OverflowMenu";
import { SummaryNodeProps } from "./Summary";
import { SummaryNode } from "./Summary";
export { SummaryNode };

export enum CardFill {
    None,
    FillRight,
    FillAll
}

interface ExpandableFormSectionProps extends FormSectionBaseProps {
    summary: React.ComponentType<SummaryNodeProps>;
    title: React.ReactNode;
    help?: React.ReactNode;
    focusOnExpandAll?: boolean;
    fillCardWidth?: CardFill;
    overflowMenuItems?: Array<MenuItem | MenuItem[]>;
}

type Props = ExpandableFormSectionProps & ExpandableProps & ExpandableContainerProps;

export class ExpandableFormSectionInternal extends FormSectionBase<Props> {
    focusControl = false;
    ignore = false;

    constructor(props: Props) {
        super(props);
        this.ignore = this.props.isExpandedByDefault; //don't focus the first time we expand due to being expandedByDefault
    }

    // we can't just use actAsExpander on card Title because it eats the click
    // and we can't add links to the title in help text or our show more info link
    handleExpand = (e: any) => {
        if (e.target.tagName.toLowerCase() !== "a") {
            e.preventDefault();

            const isSelectingText = window.getSelection().toString().length > 0;
            if (isSelectingText) {
                // usability: Don't expand/collapse in cases where the user is just trying to select/copy text
                // from our expander's title, summary or help text.
                return;
            }

            this.props.onExpandedChanged(!this.props.isExpanded);
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        if (this.props.isExpanded === false && nextProps.isExpanded === true) {
            if (this.ignore) {
                this.ignore = false;
            } else {
                this.focusControl = true;
            }
        }
    }

    render() {
        const { summary: Summary } = this.props;
        const summary = <Summary forceMobileBehaviour={this.props.forceMobileBehaviour} overflow={!!this.props.overflowMenuItems}/>;
        const subtitle = this.props.isExpanded ? this.help(this.props.help) : summary;
        const title = this.title(this.props.title, subtitle);
        const bodyclasses = cn({
            [styles.cardMedia]: true,
            [styles.cardMediaForceMobile]: this.props.forceMobileBehaviour,
            [styles.cardMediaFillRight]: this.props.fillCardWidth === CardFill.FillRight || this.props.fillCardWidth === CardFill.FillAll,
            [styles.cardMediaFillAll]: this.props.fillCardWidth === CardFill.FillAll
        });

        return <div className={styles.formExpander}>
            <div onClick={this.handleExpand}>
                {title}
            </div>
            {this.props.isExpanded && this.body(bodyclasses, true)}
            {this.moreInformation()}
        </div>;
    }

    protected title(title: React.ReactNode, subtitle: React.ReactNode) {
        return <div className={cn(styles.cardTitleContainer, this.props.forceMobileBehaviour && styles.cardTitleContainerForceMobile)}>
            <span className={cn(styles.cardTitle, this.props.forceMobileBehaviour && styles.cardTitleForceMobile)}>{title}</span>
            {subtitle}
            {this.props.overflowMenuItems && <div className={styles.overflowMenuActions}>
                <OverflowMenu menuItems={this.props.overflowMenuItems} />
            </div>}
            <div className={styles.expandCollapse}>
                <CardExpandable tabIndex={this.props.isExpanded ? -1 : 0}
                    expanded={this.props.isExpanded} />
            </div>
        </div>;
    }

    protected alterChild = (child: any): any => {
        const focusOnExpand = this.props.expandingAll === false || this.props.focusOnExpandAll;
        if (focusOnExpand && this.focusControl && this.isFocusableControl(child)) {
            this.focusControl = false;
            return React.cloneElement(child, {
                ...child.props,
                autoFocus: true
            });
        } else {
            return child;
        }
    }

    private isFocusableControl = (child: any) => {
        return child.props && (child.props.onChange || child.props.setProperties) && !child.props.doNotAutoFocus;
    }
}

const ExpandableFormSection = Expandable<ExpandableFormSectionProps>(ExpandableFormSectionInternal);
export { ExpandableFormSectionProps };
export default ExpandableFormSection;
